import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Spinner from 'react-bootstrap/Spinner';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrMonth } from '../../shared/functions/dateProcessor';
import {
  filterStorageTypeMap,
  TABLE_REFETCH_INTERVAL_MILISEC,
  TrackerDataType,
  visibleContentLogCols,
} from '../../shared/constants/constants';
import isFilterEmpty from '../../shared/functions/isFilterEmpty';
import dayjs from '../../utils/dayjs';
import MonthSelector from '../TrackerTable/MonthSelector';
import TrackerTable from '../TrackerTable/TrackerTable';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import QueryAdder from '../QueryAdder/QueryAdder';
import { DropdownTable, FlatContentLog, UserMeta } from '../../shared/types';
import { useFilters, useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import { useRecentCreations } from '../../shared/hooks/useRecentCreations';
import { getFilteredContentLogs, getMonthlyContentLogs } from '../../shared/requests/contentLogsRequest';
import { useContentLogHandler } from '../../shared/eventHandler/ContentLogEventHandler';
import { useServiceLogHandler } from '../../shared/eventHandler/ServiceLogEventHandler';
import TrackerTableFilter from '../TrackerTable/TrackerTableFilter';
import { getTrackerFilterQueryFromFilters } from '../TrackerFilter/trackerFilterHelpers';
import { TrackerTableActionButtons } from '../TrackerTable/TrackerTableActionButtons';
import TrackerTableFuzzySearch from '../TrackerTable/TrackerTableFuzzySearch';

interface ContentLogPageProps {
  dropdownTable: DropdownTable;
  user: UserMeta;
}

const useStyles = makeStyles({
  tableContainer: {
    width: 'calc(100% - 60px)',
    margin: '0 auto',
    marginBottom: 30,
  },
  filterContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '1rem',
    rowGap: '0.75rem',
    marginTop: '1rem',
    marginBottom: '1rem',
    minHeight: '32px',
  },
  filterWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    rowGap: '0.75rem',
    alignItems: 'center',
  },
  actionButtonsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    flexShrink: 0,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: '1rem',
    rowGap: '0.75rem',
  },
});

export const ContentLogPage = ({ dropdownTable, user }: Readonly<ContentLogPageProps>) => {
  const classes = useStyles();
  const filtersDispatch = useFiltersDispatch();
  const { state } = useLocation();
  const recentCreations = useRecentCreations();
  const { markCreated } = recentCreations;
  const previousMonthlyIds = useRef<Set<string> | null>(null);
  const [openForm, setOpenForm] = useState(false);

  const month = sessionStorage.getItem('contentLogCurrMonth') ?? getCurrMonth();
  const [currMonth, setCurrMonth] = useState(month);
  const [data, setData] = useState<FlatContentLog[]>([]);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [selectedBulkEditRowIds, setSelectedBulkEditRowIds] = useState<string[]>([]);
  const existingFilters = useFilters();
  const queryClient = useQueryClient();
  const contentLogParams = useParams();

  const onSuccessCallback = async () => {
    await queryClient.invalidateQueries({ queryKey: ['monthlyContentLogs'] });
    await queryClient.invalidateQueries({ queryKey: ['filteredContentLogs'] });
    if (state?.queryString && state?.category) {
      await queryClient.invalidateQueries({
        queryKey: ['fuzzySearch', TrackerDataType.CONTENT_LOG, state.queryString, state.category],
      });
    }
  };

  const contentLogEventHandler = useContentLogHandler({
    recentCreations,
    onSuccessCallback,
  });
  const serviceLogEventHandler = useServiceLogHandler({
    recentCreations,
    onSuccessCallback,
  });

  const { data: monthlyContentLogs, refetch: refetchMonthlyContentLogs } = useQuery({
    queryKey: ['monthlyContentLogs', currMonth],
    queryFn: () => getMonthlyContentLogs(currMonth, dayjs.tz.guess()),
    enabled: !state && isFilterEmpty(existingFilters),
    refetchInterval: TABLE_REFETCH_INTERVAL_MILISEC,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const filtersQueryString = useMemo(
    () => getTrackerFilterQueryFromFilters(existingFilters, TrackerDataType.CONTENT_LOG),
    [existingFilters]
  );

  const { data: filteredContentLogs } = useQuery({
    queryKey: ['filteredContentLogs', filtersQueryString],
    queryFn: async () => {
      const filteredData = await getFilteredContentLogs(filtersQueryString);
      return filteredData || [];
    },
    enabled: !!filtersQueryString,
  });

  const shouldDisableMonthSwitches = () => {
    const hasFilters = Object.keys(existingFilters).length > 0;
    const isQuickSearching = state?.queryString && state?.category;
    return hasFilters || isQuickSearching;
  };

  const detailedExportContext = useMemo(() => {
    if (state?.queryString && state?.category) {
      return {
        trackerType: TrackerDataType.CONTENT_LOG,
        mode: 'fuzzy' as const,
        queryString: state.queryString,
        category: state.category,
      };
    }

    if (!isFilterEmpty(existingFilters) && filtersQueryString) {
      return {
        trackerType: TrackerDataType.CONTENT_LOG,
        mode: 'filter' as const,
        filterQueryString: filtersQueryString,
      };
    }

    return {
      trackerType: TrackerDataType.CONTENT_LOG,
      mode: 'monthly' as const,
      month: currMonth,
      timezoneOffset: dayjs.tz.guess(),
    };
  }, [currMonth, existingFilters, filtersQueryString, state]);

  const selectAllBulkEditRows = (pageRowIds: string[]) => {
    setSelectedBulkEditRowIds(pageRowIds);
  };

  const clearBulkEditSelection = () => {
    setSelectedBulkEditRowIds([]);
  };

  const handleBulkEditSelectRow = (isChecked: boolean, rowId: string) => {
    setSelectedBulkEditRowIds((prevSelectedIds) => {
      if (isChecked) {
        return [...prevSelectedIds, rowId];
      } else {
        return prevSelectedIds.filter((id) => rowId !== id);
      }
    });
  };

  useEffect(() => {
    if (contentLogParams.contentLogId) {
      filtersDispatch({
        type: 'setFilters',
        payload: {
          id: contentLogParams.contentLogId,
        },
      });
    }
  }, [contentLogParams.contentLogId, filtersDispatch]);

  useEffect(() => {
    if (!monthlyContentLogs && !filteredContentLogs) {
      return;
    }
    const storedFilter = user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.CONTENT_LOG]];
    if (!isFilterEmpty(existingFilters) && filteredContentLogs) {
      setData(filteredContentLogs);
    } else if (isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter && monthlyContentLogs) {
      setData(monthlyContentLogs);
    }
  }, [
    monthlyContentLogs,
    filteredContentLogs,
    existingFilters,
    state,
    user.nonPIUserSettings?.userStoredFilters,
    filtersQueryString,
  ]);

  useEffect(() => {
    previousMonthlyIds.current = null;
  }, [currMonth]);

  useEffect(() => {
    if (!Array.isArray(monthlyContentLogs)) {
      return;
    }
    const storedFilter = user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.CONTENT_LOG]];
    const shouldTrack = isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter;

    if (shouldTrack && previousMonthlyIds.current) {
      const prevIds = previousMonthlyIds.current;
      monthlyContentLogs.forEach((row) => {
        if (!prevIds.has(row.id)) {
          markCreated(row.id);
        }
      });
    }

    previousMonthlyIds.current = new Set(monthlyContentLogs.map((row) => row.id));
  }, [monthlyContentLogs, existingFilters, state, user.nonPIUserSettings?.userStoredFilters, markCreated]);

  useEffect(() => {
    sessionStorage.setItem('contentLogCurrMonth', currMonth);
    if (!state && isFilterEmpty(existingFilters)) {
      refetchMonthlyContentLogs();
    }
  }, [currMonth, refetchMonthlyContentLogs, state, existingFilters]);

  return (
    <Box className={classes.tableContainer}>
      <Box>
        <MonthSelector disabled={shouldDisableMonthSwitches()} currMonth={currMonth} onMonthChange={setCurrMonth} />
      </Box>
      <Box className={classes.filterContainer}>
        <Box className={classes.filterWrapper}>
          <TrackerTableFuzzySearch />
          <TrackerTableFilter
            setDataCallback={setData}
            eventHandler={contentLogEventHandler}
            dropdownTable={dropdownTable}
          />
        </Box>
        <Box className={classes.actionButtonsWrapper}>
          <TrackerTableActionButtons
            eventHandler={contentLogEventHandler}
            dropdownTable={dropdownTable}
            currMonth={currMonth}
            rows={data}
            isBulkEdit={isBulkEdit}
            detailedExportContext={detailedExportContext}
            selectedBulkEditRowIds={selectedBulkEditRowIds}
            selectedBulkEditRowsCount={selectedBulkEditRowIds.length}
            onSwitchToBulkEditView={() => {
              setIsBulkEdit(true);
            }}
            onSwitchToMonthlyView={() => {
              setIsBulkEdit(false);
              clearBulkEditSelection();
            }}
            hasAutoAdd={false}
          />
        </Box>
      </Box>
      {data && dropdownTable ? (
        <TrackerTable
          rows={data}
          dropdownTable={dropdownTable}
          visibleCols={visibleContentLogCols}
          eventHandler={contentLogEventHandler}
          linkedEventHandler={serviceLogEventHandler}
          isBulkEdit={isBulkEdit}
          selectedBulkEditRowIds={selectedBulkEditRowIds}
          onBulkEditSelectAll={(isChecked, pageRowIds) => {
            if (isChecked) {
              selectAllBulkEditRows(pageRowIds);
              return;
            }
            clearBulkEditSelection();
          }}
          onBulkEditSelectRow={handleBulkEditSelectRow}
        />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '2rem' }}>
          <Spinner animation="border" />
        </Box>
      )}
      <QueryAdderDrawer openForm={openForm} setOpenForm={setOpenForm}>
        <QueryAdder
          title={contentLogEventHandler.newItemModalTitle}
          dropdownTable={dropdownTable}
          currMonth={currMonth}
          setOpenForm={setOpenForm}
          eventHandler={contentLogEventHandler}
        />
      </QueryAdderDrawer>
    </Box>
  );
};
